import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ArrowLeft, FileText, ShieldCheck, BookBookmark, Prohibit, SignOut, Question, CheckCircle, BookmarkSimple, Crown, MusicNotes, Shield, MagicWand, Coins, CurrencyDollar, Waveform, UsersThree, BellRinging, Sparkle, Microphone, Gift, FilmStrip, MagnifyingGlass } from "@phosphor-icons/react";
import DigestSchedulePicker from "../components/DigestSchedulePicker";

const NOTIF_LABELS = {
  like: "Likes on your videos",
  comment: "Comments",
  follow: "New followers",
  gift: "Gifts on posts",
  live_gift: "Gifts during your streams",
  live_start: "Creators you follow going live",
  room_start: "Paid & members rooms starting",
  subscribe: "New subscribers",
  mention: "Mentions",
  message: "Direct messages",
  voice_reaction: "Voice drop reactions",
  digest: "Weekly digest highlights",
  milestone: "Earnings milestones unlocked",
  streak_reminder: "Comeback bonus · streak about to break",
};

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [searchSug, setSearchSug] = useState(null);

  useEffect(() => {
    api.get("/blocks/me").then((r) => setBlocks(r.data)).catch(() => {});
    api.get("/users/me/notification-prefs").then((r) => setPrefs(r.data.prefs)).catch(() => {});
    api.get("/users/me/privacy").then((r) => setSearchSug(r.data.search_suggestions !== false)).catch(() => {});
  }, []);

  const toggleSearchSug = async () => {
    const next = !searchSug;
    setSearchSug(next);
    try { await api.put("/users/me/privacy", { search_suggestions: next }); } catch (e) { setSearchSug(!next); }
  };

  const togglePref = async (t) => {
    const next = { ...prefs, [t]: !prefs[t] };
    setPrefs(next);
    try { await api.put("/users/me/notification-prefs", { prefs: next }); } catch (e) { /* revert silently */ }
  };

  const unblock = async (uid) => {
    await api.post(`/users/${uid}/block`);
    const { data } = await api.get("/blocks/me");
    setBlocks(data);
  };

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-8" data-testid="settings-page">
      <Link to="/profile" className="inline-flex items-center gap-2 text-cream/60 hover:text-cream text-xs tracking-[0.2em] uppercase font-bold mb-4">
        <ArrowLeft size={14} /> Profile
      </Link>
      <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Settings</p>
      <h1 className="text-cream text-4xl font-black tracking-tighter mt-1">Manage account</h1>

      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-2">Creator tools</p>
        <div className="space-y-2">
          <Row to="/studio" icon={MagicWand} label="Creator Studio · Text to video" testid="settings-studio" />
          <Row to="/ai-voice" icon={Sparkle} label="AI Voice Studio · ElevenLabs text-to-speech" testid="settings-ai-voice" />
          <Row to="/ai-voiceover" icon={Microphone} label="AI Voiceover · Narrate your videos" testid="settings-ai-voiceover" />
          <Row to="/ai-video" icon={FilmStrip} label="AI Text-to-Video · Script becomes a narrated video" testid="settings-ai-video" />
          <Row to="/ai-song" icon={MusicNotes} label="AI Song Studio · Generate an original song" testid="settings-ai-song" />
          <Row to="/gifts" icon={Gift} label="Gift Shop · Browse & create virtual gifts" testid="settings-gifts" />
          <Row to="/analytics" icon={CheckCircle} label="Analytics · Views, watch time, gift earnings" testid="settings-analytics" />
          <Row to="/voice" icon={Waveform} label="Voice Studio · Record, upload, enhance" testid="settings-voice" />
          <Row to="/rooms" icon={UsersThree} label="Rooms & Meetings · Conferences, workshops, classes" testid="settings-rooms" />
          <Row to="/shop" icon={Coins} label="Buy coins · VIP" testid="settings-shop" />
          <Row to="/cashout" icon={CurrencyDollar} label="Cash out pearls" testid="settings-cashout" />
        </div>
      </section>

      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-2">Your account</p>
        <div className="space-y-2">
          <Row to="/verify" icon={CheckCircle} label={user?.is_verified ? "Verified — Manage" : "Get Verified ($5/mo)"} testid="settings-verify" />
          <Row to="/saves" icon={BookmarkSimple} label="Saved videos" testid="settings-saves" />
          <Row to="/music" icon={MusicNotes} label="Music library" testid="settings-music" />
          {user?.role === "admin" && (
            <Row to="/admin" icon={Crown} label="Admin Dashboard" testid="settings-admin" />
          )}
        </div>
      </section>

      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-2">Legal & transparency</p>
        <div className="space-y-2">
          <Row to="/guidelines" icon={BookBookmark} label="Community Guidelines" testid="settings-guidelines" />
          <Row to="/terms" icon={FileText} label="Terms of Service" testid="settings-terms" />
          <Row to="/privacy" icon={ShieldCheck} label="Privacy Policy" testid="settings-privacy" />
          <Row to="/data-safety" icon={Shield} label="Data Safety" testid="settings-data-safety" />
          <Row to="/gift-creator-agreement" icon={Gift} label="Gift Creator Agreement" testid="settings-gift-agreement" />
        </div>
      </section>

      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-2">Notifications</p>
        {!prefs ? (
          <p className="text-cream/40 text-sm">Loading…</p>
        ) : (
          <div className="space-y-2" data-testid="notification-prefs">
            {Object.keys(NOTIF_LABELS).map((t) => (
              <div key={t} className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/5">
                <div className="flex items-center gap-3 min-w-0">
                  <BellRinging size={18} className={prefs[t] ? "text-papaya" : "text-cream/30"} weight="duotone" />
                  <span className={`font-bold text-sm truncate ${prefs[t] ? "text-cream" : "text-cream/40"}`}>{NOTIF_LABELS[t]}</span>
                </div>
                <button
                  onClick={() => togglePref(t)}
                  data-testid={`notif-pref-${t}`}
                  aria-pressed={!!prefs[t]}
                  className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${prefs[t] ? "bg-papaya" : "bg-white/15"}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-ink transition-all ${prefs[t] ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </div>
            ))}
            <DigestSchedulePicker />
          </div>
        )}
      </section>

      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-2">Privacy</p>
        <div className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/5" data-testid="privacy-search-suggestions">
          <div className="flex items-center gap-3 min-w-0">
            <MagnifyingGlass size={18} className={searchSug ? "text-papaya" : "text-cream/30"} weight="duotone" />
            <div className="min-w-0">
              <span className={`font-bold text-sm block ${searchSug ? "text-cream" : "text-cream/40"}`}>Search suggestions</span>
              <span className="text-cream/40 text-xs">Show trending tags & who-to-follow before you type</span>
            </div>
          </div>
          <button
            onClick={toggleSearchSug}
            data-testid="toggle-search-suggestions"
            aria-pressed={!!searchSug}
            disabled={searchSug === null}
            className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${searchSug ? "bg-papaya" : "bg-white/15"}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-ink transition-all ${searchSug ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
      </section>

      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-2">Blocked accounts</p>
        {blocks.length === 0 ? (
          <p className="text-cream/40 text-sm">You haven't blocked anyone.</p>
        ) : (
          <div className="space-y-2" data-testid="blocks-list">
            {blocks.map((b) => (
              <div key={b.blocked_id} className="flex items-center justify-between p-3 rounded-2xl bg-white/5 border border-white/5">
                <code className="text-cream text-xs">{b.blocked_id.slice(0, 8)}…</code>
                <button onClick={() => unblock(b.blocked_id)} className="text-papaya text-xs font-black tracking-widest uppercase">Unblock</button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-2">Account</p>
        <div className="space-y-2">
          <a href="mailto:support@giffwurldaunce.com" className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/40 transition-colors text-cream">
            <Question size={20} className="text-papaya" weight="duotone" />
            <span className="font-bold">Contact support</span>
          </a>
          <button
            data-testid="settings-logout"
            onClick={doLogout}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 transition-colors text-red-300"
          >
            <SignOut size={20} weight="duotone" />
            <span className="font-bold">Log out</span>
          </button>
        </div>
      </section>
    </div>
  );
}

function Row({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/40 transition-colors text-cream">
      <Icon size={20} className="text-papaya" weight="duotone" />
      <span className="font-bold flex-1">{label}</span>
    </Link>
  );
}
